import React, { useState, useEffect } from 'react';

import { Button, InfoNote, Text } from '@schoolinks/ui-components';

import { arrayOf, shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Checkbox from 'components/Common/Checkbox';
import Modal from 'components/Common/ModalV2';
import Api from 'core/api';

import useModal from 'hooks/useModal';
import useMutation from 'hooks/useMutation';

import { ensureArray, addOrRemove, isNonEmptyArray } from 'utils/arrays';

import s from './DownloadSurveyModal.css';

const DownloadSurveyModal = ({ reports }) => {
  const { hideModal } = useModal();
  const [checkedReports, setCheckedReports] = useState([]);
  const [showError, setShowError] = useState(false);

  const {
    id: studentId,
    firstName,
    lastName,
  } = useSelector(({ casefile }) => casefile?.studentDetails);
  const { t } = useTranslation();

  const { mutate: downloadSurveyReport } = useMutation({
    apiFunc: Api.swipe.downloadBulkReports,
    options: {
      queryKey: 'Api.swipe.downloadBulkReports',
    },
    successSnackbarMessage: t('Assessments.DownloadSurveyModal.CheckYourEmail'),
    errorSnackbarMessage: t(
      'Assessments.DownloadSurveyModal.ThereWasAnIssueCreatingYourReport',
    ),
  });

  useEffect(() => {
    if (isNonEmptyArray(checkedReports) && showError) {
      setShowError(false);
    }
  }, [checkedReports, showError]);

  const isAllReportsSelected =
    checkedReports.length === ensureArray(reports).length;

  const onSelectAll = () => {
    const newSelection = isAllReportsSelected
      ? []
      : ensureArray(reports).map(({ value }) => value);
    setCheckedReports(newSelection);
  };

  const downloadReports = () => {
    if (!isNonEmptyArray(checkedReports)) {
      setShowError(true);
    } else {
      hideModal();
      downloadSurveyReport({
        data: {
          students: [studentId],
          type: checkedReports.join(','),
        },
      });
    }
  };

  const modalTitle = `Generate Reports for ${firstName} ${lastName}`;

  return (
    <Modal
      onClose={hideModal}
      title={modalTitle}
      classes={{ paperScrollPaper: s.paperScrollPaper }}
      actions={
        <>
          <Button
            color='secondary'
            label={t('Assessments.DownloadSurveyModal.Cancel')}
            onClick={hideModal}
            variant="outlined"
          />

          <Button
            label={t('Assessments.DownloadSurveyModal.Download')}
            onClick={downloadReports}
            variant="filled"
          />
        </>
      }
    >
      <div className={s.content}>
        <div className={s.header}>
          <Checkbox
            label={t('Assessments.DownloadSurveyModal.SelectAll')}
            value={isAllReportsSelected}
            checked={isAllReportsSelected}
            onChange={onSelectAll}
            classes={{ root: s.checkboxRoot }}
            checkBoxClasses={{ root: s.checkbox }}
          />
        </div>
        <div className={s.bucket}>
          {ensureArray(reports).map(({ label, value }) => (
            <div key={label} className={s.report}>
              <Checkbox
                label={label}
                checked={checkedReports.includes(value)}
                onChange={() =>
                  setCheckedReports(addOrRemove(checkedReports, value))
                }
                classes={{ root: s.checkboxRoot }}
                checkBoxClasses={{ root: s.checkbox }}
              />

              <Text color="grayDark">PDF</Text>
            </div>
          ))}
        </div>
        {showError ? (
          <InfoNote containerClass={s.errorInfo} size='default' variant="error">
            {t('Assessments.DownloadSurveyModal.YouMustSelectAt')}
          </InfoNote>
        ) : null}
      </div>
    </Modal>
  );
};

DownloadSurveyModal.propTypes = {
  reports: arrayOf(shape({})).isRequired,
};

export default DownloadSurveyModal;
